import axios from "axios";

const getExtractorBaseUrl = () => {
  let url = process.env.REACT_APP_EXTRACTOR_API || "https://aakashgangaopen.xyz/research-paper";
  return url.replace(/\/+$/, "");
};

/**
 * Intelligently parses extraction API errors into clear, user-friendly messages.
 * @param {any} error 
 * @returns {string} User-friendly error message
 */
export const parseExtractorError = (error) => {
  if (!error) return "An unexpected error occurred during manuscript extraction. Please try again.";

  let rawMessage = "";

  if (typeof error === "string") {
    rawMessage = error;
  } else if (error?.response) {
    const data = error.response.data;
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        rawMessage = parsed.detail || parsed.message || parsed.error || data;
      } catch (e) {
        rawMessage = data;
      }
    } else if (data && typeof data === "object") {
      rawMessage = data.detail || data.message || data.error || JSON.stringify(data);
    } else {
      rawMessage = error.message || `Server error (${error.response.status})`;
    }
  } else {
    rawMessage = error?.message || String(error);
  }

  if (typeof rawMessage === "object") {
    rawMessage = JSON.stringify(rawMessage);
  }

  const lowerMsg = String(rawMessage).toLowerCase();

  // Handle Anthropic / AI Server overloaded errors
  if (
    lowerMsg.includes("overloaded_error") ||
    lowerMsg.includes("overloaded") ||
    lowerMsg.includes("503 service unavailable")
  ) {
    return "The AI manuscript extraction engine is currently busy or overloaded. Please wait a minute and try again.";
  }

  // Handle Rate Limits
  if (
    lowerMsg.includes("rate_limit") ||
    lowerMsg.includes("429") ||
    lowerMsg.includes("too many requests")
  ) {
    return "Too many requests sent to the extraction server. Please wait a moment and try again.";
  }

  // Handle Timeouts
  if (
    lowerMsg.includes("timeout") ||
    lowerMsg.includes("econnaborted") ||
    lowerMsg.includes("timed out")
  ) {
    return "Manuscript extraction timed out. Please check your file and try again.";
  }

  // Handle File format / document reading errors
  if (
    lowerMsg.includes("invalid pdf") ||
    lowerMsg.includes("corrupt") ||
    lowerMsg.includes("cannot read")
  ) {
    return "The uploaded document could not be processed. Please ensure it is a valid PDF or Word document.";
  }

  // Handle 500 / internal server error or stringified Python dicts/JSON
  if (
    error?.response?.status === 500 ||
    lowerMsg.includes("500 internal server error") ||
    lowerMsg.includes("type': 'error'") ||
    lowerMsg.includes('"type": "error"') ||
    lowerMsg.startsWith("{'detail'") ||
    lowerMsg.startsWith('{"detail"')
  ) {
    const match = String(rawMessage).match(/['"]message['"]:\s*['"]([^'"]+)['"]/);
    if (match && match[1] && match[1] !== "Overloaded") {
      return `Extraction server error: ${match[1]}`;
    }
    return "Server error occurred during document extraction. Please wait a few moments and try again.";
  }

  // If rawMessage is clean, short, and doesn't look like technical json/python dict representation
  if (
    rawMessage &&
    rawMessage.length < 120 &&
    !rawMessage.includes("{") &&
    !rawMessage.includes("Request failed with status code")
  ) {
    return rawMessage;
  }

  return "Failed to process document. Please try again in a few moments.";
};

/**
 * Step 1: Extract JATS XML from uploaded PDF file
 * Endpoint: POST /extract
 * Payload: multipart/form-data with key 'pdf_file'
 * Response: JATS XML string
 */
export const extractManuscriptXml = async (file) => {
  const baseUrl = getExtractorBaseUrl();
  const formData = new FormData();
  formData.append("pdf_file", file);

  const response = await axios.post(`${baseUrl}/extract`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    timeout: 900000, // 15 minutes timeout for heavy LLM processing
    responseType: "text",
  });

  return response.data;
};

/**
 * Step 2: Generate PDF from JATS XML string/file
 * Endpoint: POST /generate_pdf
 * Payload: multipart/form-data with keys 'xml_file' and 'image_map'
 * Response: PDF binary Blob (application/pdf)
 */
export const generatePdfFromXml = async (xmlData, imageMap = {}) => {
  const baseUrl = getExtractorBaseUrl();
  const formData = new FormData();

  let xmlBlob;
  if (xmlData instanceof Blob) {
    xmlBlob = xmlData;
  } else if (typeof xmlData === "string") {
    xmlBlob = new Blob([xmlData], { type: "application/xml" });
  } else {
    xmlBlob = new Blob([String(xmlData)], { type: "application/xml" });
  }

  const xmlFile = new File([xmlBlob], "manuscript.xml", { type: "application/xml" });
  formData.append("xml_file", xmlFile);

  const imageMapStr =
    typeof imageMap === "string" ? imageMap : JSON.stringify(imageMap || {});
  formData.append("image_map", imageMapStr);

  const response = await axios.post(`${baseUrl}/generate_pdf`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    timeout: 900000, // 15 minutes timeout
    responseType: "blob",
  });

  return response.data; // Blob
};
